import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import AltinSozlesmeDialog from "@/components/AltinSozlesmeDialog";
import { Coins, Plus, ChevronRight, Wallet } from "lucide-react";

const gr = (n) => (n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const money = (n) => (n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AltinCari() {
  const [rows, setRows] = useState([]);
  const [kasa, setKasa] = useState(null);
  const [dlg, setDlg] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const [c, k] = await Promise.all([api.get("/altin/customers"), api.get("/altin/kasa")]);
      setRows(c.data || []);
      setKasa(k.data || null);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="kicker text-[#B8933F] flex items-center gap-2"><Coins className="w-4 h-4" /> ALTIN CARİ</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-[#26231E]">Altın Müşterileri</h1>
          <p className="text-sm text-[#8C8377] mt-1">Sözleşme ve taksitler USD; bakiye ve kasa gram altın cinsinden.</p>
        </div>
        <button onClick={() => setDlg(true)} data-testid="altin-new-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150">
          <Plus className="w-4 h-4" /> Yeni Altın Sözleşmesi
        </button>
      </div>

      {/* Altın Kasa özeti */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#26231E] text-white rounded-sm p-4" data-testid="altin-kasa-gram">
          <div className="kicker text-[#B8933F] flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> KASA (GRAM ALTIN)</div>
          <div className="font-heading font-bold text-2xl mt-1 tabular-nums">{gr(kasa?.kasa_gram)} <span className="text-sm font-normal">gr</span></div>
        </div>
        <Card label="KASA ₺ KARŞILIĞI" value={`${money(kasa?.kasa_try_karsiligi)} ₺`} />
        <Card label="KASA $ KARŞILIĞI" value={`${money(kasa?.kasa_usd_karsiligi)} $`} />
        <Card label="GÜNCEL USD/GRAM" value={`${money(kasa?.guncel_usd_gram)} $`} accent="#B8933F" />
      </div>

      <div className="bg-white border border-[#E7DECC] rounded-sm overflow-auto" data-testid="altin-cari-table">
        <table className="w-full text-sm">
          <thead className="bg-[#F3ECDD]">
            <tr className="border-b border-[#E7DECC]">
              <th className="px-3 py-2.5 kicker text-left">Müşteri</th>
              <th className="px-3 py-2.5 kicker text-right">Satış (gr)</th>
              <th className="px-3 py-2.5 kicker text-right">Tahsilat (gr)</th>
              <th className="px-3 py-2.5 kicker text-right">Kalan (gr)</th>
              <th className="px-3 py-2.5 kicker text-right">Toplam USD</th>
              <th className="px-3 py-2.5 kicker text-right">Ödenen USD</th>
              <th className="px-3 py-2.5 kicker text-center">Vade</th>
              <th className="px-3 py-2.5 kicker text-right"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-[#8C8377]">Henüz altın müşterisi yok. "Yeni Altın Sözleşmesi" ile ekleyin.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.tc_no + r.musteri} onClick={() => nav(`/altin-cari/${encodeURIComponent(r.musteri)}`)} data-testid={`altin-cari-row`} className="border-b border-[#E7DECC] hover:bg-[#F3ECDD] cursor-pointer">
                <td className="px-3 py-2 font-medium">{r.musteri}{r.vadesi_gecti > 0 && <span className="ml-2 text-xs text-[#A63A3A]">● {r.vadesi_gecti} gecikmiş</span>}</td>
                <td className="px-3 py-2 text-right tabular-nums">{gr(r.toplam_satis_gram)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-[#2F6E4E]">{gr(r.tahsilat_gram)}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${r.kalan_gram > 0 ? "text-[#A63A3A]" : "text-[#2F6E4E]"}`}>{gr(r.kalan_gram)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(r.toplam_usd)} $</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(r.odenen_usd)} $</td>
                <td className="px-3 py-2 text-center">{r.vade_sayisi}</td>
                <td className="px-3 py-2 text-right"><ChevronRight className="w-4 h-4 text-[#8C8377] inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AltinSozlesmeDialog open={dlg} onClose={() => setDlg(false)} onDone={load} />
    </Layout>
  );
}

function Card({ label, value, accent }) {
  return (
    <div className="bg-white border border-[#E7DECC] rounded-sm p-4">
      <div className="kicker">{label}</div>
      <div className="font-heading font-bold text-lg mt-1 tabular-nums" style={accent ? { color: accent } : undefined}>{value}</div>
    </div>
  );
}
